/* Copyright (c) 2017 Backpack Health LLC */

/*  global window */
import { authorizedApiRequest, apiRequest, authorizedApiRequestBlob, getBaseUrl } from './ApiRequest';
import ApiErrorModel from '../models/ApiErrorModel';
import ShareCard from '../models/ShareCardModel';
import { authToken } from '../utilities/LocalStorage';

const ShareCardApi = {
  getAll: function getAllShareCards(profileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/shareCards`);
  },
  create: function createShareCard(profileId) {
    return authorizedApiRequest('post', `profiles/${profileId}/shareCards`);
  },
  get: function getShareCard(profileId, shareCardId) {
    return authorizedApiRequest('get', `profiles/${profileId}/shareCards/${shareCardId}`);
  },
  update: function updateShareCard(profileId, shareCardId) {
    return authorizedApiRequest('put', `profiles/${profileId}/shareCards/${shareCardId}`);
  },
  del: function updateShareCard(profileId, shareCardId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/shareCards/${shareCardId}`);
  },
  download: function downloadPDF(profileId, shareCardId, languageCode) {
    const languageParam = languageCode ? `language_code=${languageCode}` : '';
    return authorizedApiRequestBlob('get', `profiles/${profileId}/shareCards/${shareCardId}/pdfDocument?${languageParam}`);
  },
  publicMetadata: function getPublicMetadata(hash, languageCode) {
    const languageParam = languageCode ? `?language_code=${languageCode}` : '';
    return apiRequest('get', `public/shareCards/${hash}/metadata${languageParam}`);
  },
  publicPdfDocument: function getPublicPdf(hash, languageCode) {
    const languageParam = languageCode ? `language_code=${languageCode}` : '';
    return apiRequest('get', `public/shareCards/${hash}/pdfDocument?include_share_url=true&${languageParam}`);
  },
  publicFile: function getPublicFile(hash, fileId) {
    return apiRequest('get', `public/shareCards/${hash}/files/${fileId}`);
  },
  publicFileDownloadUrl: function getPublicFileUrl(hash, fileId) {
    return apiRequest('get', `public/shareCards/${hash}/files/${fileId}/downloadUrl`);
  },
  setPasscode: function setPasscode(profileId, shareCardId) {
    return authorizedApiRequest('put', `profiles/${profileId}/shareCards/${shareCardId}/accessCode`);
  },
};

function withPasscodeAuthorization(encodedPasscode) {
  return encodedPasscode ? { Authorization: `Basic ${encodedPasscode}` } : {};
}

const ShareCardService = {
  all(profileId, callback) {
    return ShareCardApi.getAll(profileId)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const shareCardCollection = apiError ? [] : res.body.data;
      callback(apiError, shareCardCollection);
    });
  },

  create(profileId, shareCardData, callback) {
    return ShareCardApi.create(profileId)
    .send(shareCardData)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const sc = apiError ? {} : res.body.data;
      callback(apiError, sc);
    });
  },

  get(profileId, cardId, callback) {
    return ShareCardApi.get(profileId, cardId)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const sc = apiError ? {} : res.body.data;
      callback(apiError, sc);
    });
  },

  update(profileId, shareCardData, callback) {
    return ShareCardApi.update(profileId, shareCardData.id)
    .send(shareCardData)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  del(profileId, cardId, callback) {
    return ShareCardApi.del(profileId, cardId)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  // download(profileId, cardId, languageCode, callback) {
  //   return ShareCardApi.download(profileId, cardId, languageCode)
  //   .end((err, res) => {
  //     const disposition = res.xhr.getResponseHeader('Content-Disposition');
  //     const filename = disposition.match(/filename="(.+)"/)[1];
  //     const apiError = err ? new ApiErrorModel(err) : null;
  //     const blob = new window.Blob([res.xhr.response], { type: 'application/pdf' });
  //     callback(apiError, filename, blob);
  //   });
  // },

  getPdfUrl(profileId: number, shareCardId: number, languageCode: string, include_share_url: boolean): string {
    const languageParam = languageCode ? `&language_code=${languageCode}` : '';
    const includeShareUrl = include_share_url ? `&include_share_url=${include_share_url}` : '';
    return `${getBaseUrl()}/profiles/${profileId}/shareCards/${shareCardId}/pdfDocument?token=${authToken.get()}${languageParam}${includeShareUrl}`;
  },

  async getDefinitions(profileId) {
    try {
      const { body: { data } } = await authorizedApiRequest('get', `/profiles/${profileId}/shareCards/definitions`);
      return data;
    } catch(e) {
      throw new ApiErrorModel(e);
    }
  },

  getPublicMetadata(hash, encodedPasscode, languageCode, callback) {
    return encodedPasscode
      ? ShareCardApi.publicMetadata(hash, languageCode)
        .set(withPasscodeAuthorization(encodedPasscode))
        .end((err, res) => {
          err ? callback(new ApiErrorModel(err), {}) : callback(null, res.body.data);
        })
      : ShareCardApi.publicMetadata(hash, languageCode)
        .end((err, res) => {
          err ? callback(new ApiErrorModel(err), {}) : callback(null, res.body.data);
        });
  },

  getPublicPdf(hash, encodedPasscode, languageCode): Promise<{ blob, filename }> {
    return new Promise((resolve, reject) => {
      const apiCall = ShareCardApi.publicPdfDocument(hash, languageCode);

      if (encodedPasscode) {
        apiCall.set(withPasscodeAuthorization(encodedPasscode));
      }

      apiCall.responseType('blob')
        .end((err, res) => {
          if (err) {
            return reject(new ApiErrorModel(err));
          }

          const disposition = res.xhr.getResponseHeader('Content-Disposition');
          const filename = disposition.match(/filename="(.+)"/)[1];
          const blob = new window.Blob([res.body], { type: 'application/pdf' });

          resolve({ blob, filename });
        });
    });
  },

  getPublicFile(hash, fileId, encodedPasscode, callback) {
    return ShareCardApi.publicFile(hash, fileId)
      .set(withPasscodeAuthorization(encodedPasscode))
      .end((err, res) => {
        err ? callback(new ApiErrorModel(err), {}) : callback(null, res.body.data);
      });
  },

  getPublicFilePreviewUrl(hash, fileId, encodedPasscode, callback) {
    return ShareCardApi.publicFileDownloadUrl(hash, fileId)
      .set(withPasscodeAuthorization(encodedPasscode))
      .query({ inline: true })
      .end((err, res) => {
        err ? callback(new ApiErrorModel(err), {}) : callback(null, res.body.url);
      });
  },

  setPasscode(profileId, shareCardId, passcode, callback) {
    return ShareCardApi.setPasscode(profileId, shareCardId)
      .send({ access_code: passcode })
      .end((err) => {
        err ? callback(new ApiErrorModel(err), null) : callback(null, null);
      });
  },

  getShareCardColor(profileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/shareCards/colorShareCard`)
      .then(response => response.body);
  },

};

export default ShareCardService;
